import { shallowEqual } from 'react-redux';

import { Alert, Box, Icon, List, Text, TextButton } from '@zillow/constellation-10';
import { IconLightBulbFilled } from '@zillow/constellation-icons';

import { useSelector } from '../../selectors/useSelector';

import { RECS, RECS_ARRAY_ORDER, RecsLearnMoreConstants } from '../../constants/RecommendationsConstants';

import Add3DToursCSEvents from '../../model/ClickstreamEvent/Add3DToursCSEvents';

type RecommendationsAlertProps = {
  sectionName: string;
  onRecsClick?: (recKey: keyof typeof RecsLearnMoreConstants) => void;
  showLearnMore?: boolean;
};

const RecommendationsAlert = ({
  sectionName,
  onRecsClick = () => {},
  showLearnMore = true,
}: RecommendationsAlertProps) => {
  const { currentProperty, recommendations, user } = useSelector(
    (state) => ({
      currentProperty: state?.properties?.currentProperty,
      recommendations: state?.properties?.recommendations,
      user: state?.user,
    }),
    shallowEqual,
  );

  if (!recommendations?.[sectionName]) {
    return null;
  }

  const unsortedRecKeys = Object.keys(recommendations[sectionName]);

  if (unsortedRecKeys.length === 0) {
    return null;
  }

  const enforceSortedKeys = RECS_ARRAY_ORDER.hasOwnProperty(sectionName);
  const sortedRecKeys: Array<keyof typeof RecsLearnMoreConstants> = [];

  if (enforceSortedKeys) {
    unsortedRecKeys.forEach((recKey) => (sortedRecKeys[RECS_ARRAY_ORDER[sectionName][recKey]] = recKey));
  }

  const keysToIterateOver = enforceSortedKeys ? sortedRecKeys.filter(Boolean) : unsortedRecKeys;

  return (
    <Box css={{ marginBottom: '400' }}>
      <Alert.Root
        tone="info"
        css={{
          // eslint-disable-next-line curly-quotes/no-straight-quotes
          '& > [data-c11n-component="Alert.Icon"]': {
            display: 'none',
          },
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexShrink: 0,
            alignItems: 'flex-start',
            marginInlineEnd: '300',
          }}
        >
          <Icon render={<IconLightBulbFilled />} size="md" css={{ color: 'icon.accent.brand.hero' }} />
        </Box>

        <Alert.Body>
          {keysToIterateOver.length === 1 ? (
            <>
              <Text>{RecsLearnMoreConstants[keysToIterateOver[0]].heading}</Text>

              {showLearnMore && (
                <TextButton
                  css={{ marginLeft: '200' }}
                  data-eventid={
                    keysToIterateOver[0] === RECS.VIRTUAL_TOUR ? Add3DToursCSEvents.LearnMore.data_event_id : undefined
                  }
                  onClick={() => {
                    if (keysToIterateOver[0] === RECS.VIRTUAL_TOUR) {
                      Add3DToursCSEvents.LearnMore({ currentProperty, user });
                    }
                    onRecsClick(keysToIterateOver[0]);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </>
          ) : (
            <List.Root>
              {keysToIterateOver.map((recKey, idx) => (
                <List.Item key={idx} css={{ paddingBlock: '0' }}>
                  <Text>{RecsLearnMoreConstants[recKey].heading}</Text>

                  <TextButton
                    css={{ marginLeft: '200' }}
                    data-eventid={recKey === RECS.VIRTUAL_TOUR ? Add3DToursCSEvents.LearnMore.data_event_id : undefined}
                    onClick={() => {
                      if (recKey === RECS.VIRTUAL_TOUR) {
                        Add3DToursCSEvents.LearnMore({ currentProperty, user });
                      }
                      onRecsClick(recKey);
                    }}
                  >
                    Learn more
                  </TextButton>
                </List.Item>
              ))}
            </List.Root>
          )}
        </Alert.Body>
      </Alert.Root>
    </Box>
  );
};

export default RecommendationsAlert;
